/**
 * Invoices > Finance charges (tracker item 28).
 *
 * An office user picks an as-of date, previews the late fees the server would
 * charge, selects the customers to charge and assesses them. Each charge is
 * its own FINANCE_CHARGE invoice, posted at once. Every amount shown comes
 * from the server; the only client math is the total of the selected rows.
 *
 * Route-gated to ADMIN / OFFICE_MANAGER, like the backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on /api/finance-charges.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IFinanceChargesApi } from '../api';
import * as types from '../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { invoiceDate, invoiceMoney } from './invoiceUi';
import {
  COMPANY_SETTINGS_PATH,
  FinanceChargeErrorView,
  asOfDateError,
  assessConfirmMessage,
  chargeableCustomerIds,
  financeChargeError,
  financeChargeSettingsSummary,
  maxFinanceChargeDate,
  selectedChargeTotal,
  skipReasonLabel,
  toggleAll,
  toggleSelection,
} from './financeChargeUi';
import { Toast } from '../components/Toast';

const cell = (numeric = false) => `px-4 py-3 ${numeric ? 'text-right tabular-nums whitespace-nowrap' : 'text-left'}`;
const COLUMN_COUNT = 7;

function ErrorBox({ error }: { error: FinanceChargeErrorView }) {
  return (
    <Toast kind="error" id={`${error.message}|${error.settingsLink ? 'settings' : ''}`}>
      <p>{error.message}</p>
      {error.settingsLink && (
        <Link to={COMPANY_SETTINGS_PATH} className="mt-2 inline-block font-semibold text-red-900 underline">
          Open company settings
        </Link>
      )}
    </Toast>
  );
}

function PreviewRow({ row, selected, disabled, onToggle }: {
  row: types.FinanceChargePreviewRow;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailId = `finance-charge-invoices-${row.customerId}`;
  return (
    <>
      <tr className={row.chargeable ? undefined : 'text-gray-500'}>
        <td className="px-4 py-3 w-10">
          {row.chargeable && (
            <input
              type="checkbox"
              checked={selected}
              disabled={disabled}
              onChange={onToggle}
              aria-label={`Charge ${row.customerName}`}
              className="h-5 w-5 md:h-4 md:w-4 rounded border-gray-300"
            />
          )}
        </td>
        <td className={cell()} data-label="Customer" data-primary>
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={detailId}
              aria-label={`${expanded ? 'Hide' : 'Show'} overdue invoices for ${row.customerName}`}
              disabled={row.invoices.length === 0}
              onClick={() => setExpanded((value) => !value)}
              className="w-6 max-md:h-11 max-md:w-11 max-md:shrink-0 text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:cursor-default"
            >
              {expanded ? '▾' : '▸'}
            </button>
            <Link to={`/customers/${row.customerId}`} className="min-w-0 break-words font-semibold text-blue-700 hover:underline">{row.customerName}</Link>
          </div>
        </td>
        <td className={cell(true)} data-label="Overdue balance">{invoiceMoney(row.overdueBalance)}</td>
        <td className={cell(true)} data-label="Unapplied credit">{invoiceMoney(row.unappliedCredit)}</td>
        <td className={cell(true)} data-label="Overdue base">{invoiceMoney(row.overdueBase)}</td>
        <td className={`${cell(true)} font-semibold`} data-label="Charge">{row.chargeable ? invoiceMoney(row.charge) : '—'}</td>
        <td className={cell()} data-label="Note">
          {row.skipReason ? (
            <span>
              <span className="font-medium">{skipReasonLabel(row.skipReason)}</span>
              {row.note && <span className="block text-xs">{row.note}</span>}
            </span>
          ) : (row.note ?? '')}
        </td>
      </tr>
      {expanded && (
        <tr id={detailId} className="bg-gray-50">
          <td colSpan={COLUMN_COUNT} className="px-4 py-3 pl-20">
            <table className="responsive-table text-sm">
              <caption className="sr-only">Overdue invoices for {row.customerName}</caption>
              <thead>
                <tr className="text-gray-600">
                  <th scope="col" className="pr-6 py-1 text-left font-medium">Invoice</th>
                  <th scope="col" className="pr-6 py-1 text-left font-medium">Due</th>
                  <th scope="col" className="pr-6 py-1 text-right font-medium">Days overdue</th>
                  <th scope="col" className="py-1 text-right font-medium">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {row.invoices.map((invoice) => (
                  <tr key={invoice.invoiceId}>
                    <td className="pr-6 py-1" data-label="Invoice" data-primary><Link to={`/invoices/${invoice.invoiceId}`} className="text-blue-700 hover:underline">{invoice.invoiceNumber}</Link></td>
                    <td className="pr-6 py-1" data-label="Due">{invoiceDate(invoice.dueDate)}</td>
                    <td className="pr-6 py-1 text-right tabular-nums" data-label="Days overdue">{invoice.daysOverdue}</td>
                    <td className="py-1 text-right tabular-nums" data-label="Remaining">{invoiceMoney(invoice.remainingBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function AssessResult({ result }: { result: types.AssessFinanceChargesResult }) {
  const count = result.assessed.length;
  return (
    <section aria-label="Assessment result" className="mb-6 rounded-lg bg-white p-5 shadow">
      <h2 className="text-lg font-semibold text-gray-900">Assessment result</h2>
      {result.runId === null || count === 0 ? (
        <><Toast kind="info">Nothing was assessed for {result.periodKey}. No invoices were created.</Toast><p className="mt-2 text-gray-700">Nothing was assessed for {result.periodKey}. No invoices were created.</p></>
      ) : (
        <>
          <Toast kind="success">Created {count} finance-charge {count === 1 ? 'invoice' : 'invoices'} totaling {invoiceMoney(result.totalAssessed)} for {result.periodKey} (as of {invoiceDate(result.asOfDate)}).</Toast>
          <p className="mt-2 text-green-800">
            Created {count} finance-charge {count === 1 ? 'invoice' : 'invoices'} totaling {invoiceMoney(result.totalAssessed)} for {result.periodKey} (as of {invoiceDate(result.asOfDate)}).
          </p>
        </>
      )}
      {count > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {result.assessed.map((entry) => (
            <li key={entry.invoiceId}>
              <Link to={`/invoices/${entry.invoiceId}`} className="font-semibold text-blue-700 hover:underline">#{entry.invoiceNumber}</Link>
              {' '}{entry.customerName}: {invoiceMoney(entry.amount)}
              <span className="text-gray-500"> on {invoiceMoney(entry.overdueBase)} overdue</span>
            </li>
          ))}
        </ul>
      )}
      {result.skipped.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-900">Not charged ({result.skipped.length})</h3>
          <ul className="mt-1 space-y-1 text-sm">
            {result.skipped.map((entry) => (
              <li key={`${entry.customerId}-${entry.reason}`} className={entry.reason === 'ERROR' ? 'text-red-700' : 'text-gray-700'}>
                {entry.customerName ?? entry.customerId}: {skipReasonLabel(entry.reason)}
                {entry.message && <span className="text-gray-500"> ({entry.message})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export function FinanceChargesPage() {
  const api = useMemo<IFinanceChargesApi>(() => createAPIClient(), []);
  const maxDate = useMemo(() => maxFinanceChargeDate(), []);
  const [asOfDate, setAsOfDate] = useState(maxDate);
  const [preview, setPreview] = useState<types.FinanceChargePreview | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [assessing, setAssessing] = useState(false);
  const [error, setError] = useState<FinanceChargeErrorView | null>(null);
  const [result, setResult] = useState<types.AssessFinanceChargesResult | null>(null);
  // Ignore a preview response that arrives after the date changed again
  const requestSeq = useRef(0);

  const dateError = asOfDateError(asOfDate, maxDate);

  const loadPreview = useCallback(async (date: string, options: { keepResult?: boolean } = {}) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    if (!options.keepResult) setResult(null);
    try {
      const next = await api.previewFinanceCharges({ asOfDate: date });
      if (seq !== requestSeq.current) return;
      setPreview(next);
      setSelected(chargeableCustomerIds(next.rows));
    } catch (cause) {
      if (seq !== requestSeq.current) return;
      setPreview(null);
      setSelected([]);
      setError(financeChargeError(cause, 'Could not load the finance charge preview'));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadPreview(maxDate);
  }, [loadPreview, maxDate]);

  function changeDate(value: string) {
    // A preview belongs to its date: never assess an old preview under a new date
    requestSeq.current += 1;
    setAsOfDate(value);
    setPreview(null);
    setSelected([]);
    setResult(null);
    setError(null);
    setLoading(false);
  }

  function handlePreview(event: React.FormEvent) {
    event.preventDefault();
    if (dateError) return;
    void loadPreview(asOfDate);
  }

  const rows = preview?.rows ?? [];
  const chargeableIds = chargeableCustomerIds(rows);
  const selectedIds = selected.filter((id) => chargeableIds.includes(id));
  const total = selectedChargeTotal(rows, selectedIds);
  const allSelected = chargeableIds.length > 0 && selectedIds.length === chargeableIds.length;
  const busy = loading || assessing;

  async function handleAssess() {
    if (!preview || !preview.enabled || selectedIds.length === 0 || assessing) return;
    if (!window.confirm(assessConfirmMessage(selectedIds.length, total, preview))) return;
    setAssessing(true);
    setError(null);
    setResult(null);
    try {
      // The date the preview was computed for, and only the rows the user reviewed
      const outcome = await api.assessFinanceCharges({ asOfDate: preview.asOfDate, customerIds: selectedIds });
      setResult(outcome);
      await loadPreview(preview.asOfDate, { keepResult: true });
    } catch (cause) {
      setError(financeChargeError(cause, 'Could not assess finance charges'));
    } finally {
      setAssessing(false);
    }
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/invoices" className="text-sm text-blue-700 hover:underline">← Invoices</Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-2">Finance charges</h1>
      <p className="mt-1 mb-6 max-w-3xl text-sm text-gray-600">
        Charge late fees on overdue balances using the rate, grace days and minimum in company settings. Preview first: nothing is
        charged until you assess. Each charge becomes its own posted invoice, and each customer is charged at most once per month.
      </p>

      <form onSubmit={handlePreview} className="mb-6 flex flex-col gap-3 rounded-lg bg-white p-4 shadow sm:p-5 sm:flex-row sm:items-end">
        <div>
          <label htmlFor="finance-charge-as-of" className={labelClass}>As of</label>
          <input
            id="finance-charge-as-of"
            type="date"
            required
            max={maxDate}
            value={asOfDate}
            onChange={(event) => changeDate(event.target.value)}
            aria-invalid={dateError ? true : undefined}
            className={inputClass}
          />
        </div>
        <button type="submit" disabled={busy || !!dateError} className={`${secondaryButtonClass} max-sm:py-2.5`}>
          {loading ? 'Loading…' : 'Preview'}
        </button>
        {dateError && <p className="text-sm text-red-700 sm:self-center">{dateError}</p>}
      </form>

      {error && <ErrorBox error={error} />}
      {result && <AssessResult result={result} />}

      {loading && !preview && <p className="text-gray-600">Loading preview…</p>}

      {preview && !preview.enabled && (
        <section role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-amber-900">
          <h2 className="font-semibold">Finance charges are turned off</h2>
          <p className="mt-1 text-sm">
            The finance charge rate in company settings is 0, so there is nothing to charge. An administrator can set a rate,
            grace period and minimum charge under <Link to={COMPANY_SETTINGS_PATH} className="font-semibold underline">Settings &gt; Company</Link>.
          </p>
        </section>
      )}

      {preview && preview.enabled && (
        <section className="rounded-lg bg-white p-4 shadow sm:p-5">
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold text-gray-900">
              Preview for {invoiceDate(preview.asOfDate)} <span className="text-sm font-normal text-gray-500">(period {preview.periodKey})</span>
            </h2>
            <p className="text-sm text-gray-600">{financeChargeSettingsSummary(preview.settings)}</p>
          </div>

          <div className="overflow-x-auto">
            <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
              <caption className="sr-only">Finance charge preview by customer</caption>
              {/* On phones the shared card CSS hides thead; keep only the select-all checkbox visible */}
              <thead className="bg-gray-50 max-md:block max-md:!static max-md:!h-auto max-md:!w-full max-md:!overflow-visible max-md:![clip:auto]">
                <tr>
                  <th scope="col" className="px-4 py-3 w-10 max-md:block max-md:w-full max-md:!px-0 max-md:!py-1 max-md:text-left">
                    <label className="inline-flex items-center gap-3 max-md:min-h-[44px]">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        disabled={busy || chargeableIds.length === 0}
                        onChange={() => setSelected(toggleAll(rows, selectedIds))}
                        aria-label="Select all chargeable customers"
                        className="h-5 w-5 md:h-4 md:w-4 rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-gray-700 md:hidden">Select all</span>
                    </label>
                  </th>
                  {[
                    { label: 'Customer' },
                    { label: 'Overdue balance', numeric: true },
                    { label: 'Unapplied credit', numeric: true },
                    { label: 'Overdue base', numeric: true },
                    { label: 'Charge', numeric: true },
                    { label: 'Note' },
                  ].map((column) => (
                    <th key={column.label} scope="col" className={`px-4 py-3 font-medium text-gray-600 max-md:hidden ${column.numeric ? 'text-right' : 'text-left'}`}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  <tr><td colSpan={COLUMN_COUNT} className="px-4 py-10 text-center text-gray-600">No customer has an overdue balance as of this date.</td></tr>
                ) : rows.map((row) => (
                  <PreviewRow
                    key={row.customerId}
                    row={row}
                    selected={selectedIds.includes(row.customerId)}
                    disabled={busy}
                    onToggle={() => setSelected((current) => toggleSelection(current, row.customerId))}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-700" aria-live="polite">
              {chargeableIds.length === 0
                ? 'Nobody can be charged for this date.'
                : <>Selected <strong>{selectedIds.length}</strong> of {chargeableIds.length} chargeable · Total <strong>{invoiceMoney(total)}</strong></>}
            </p>
            <button type="button" disabled={busy || selectedIds.length === 0} onClick={() => void handleAssess()} className={`${primaryButtonClass} max-sm:py-2.5`}>
              {assessing ? 'Assessing…' : 'Assess'}
            </button>
          </div>
        </section>
      )}
    </main>
  );
}
